import PlentyApiClient from "./base";

// PlentyArticles API
export const plentyArticlesApi = {
  // CRUD Operations
  createArticle: (articleData) => {
    return PlentyApiClient.post("/articles/", articleData);
  },

  getArticles: (params = {}) => {
    const { skip = 0, limit = 100, ...otherParams } = params;
    return PlentyApiClient.get("/articles/", {
      params: { skip, limit, ...otherParams },
    });
  },

  getArticle: (variantenId) => {
    return PlentyApiClient.get(`/articles/${variantenId}`);
  },

  getArticleHistory: (sku, days = 30) => {
    return PlentyApiClient.get(`/articles/${sku}/history`, {
      params: { days },
    });
  },

  updateArticle: (variantenId, updateData) => {
    return PlentyApiClient.put(`/articles/${variantenId}`, updateData);
  },

  deleteArticle: (variantenId) => {
    return PlentyApiClient.delete(`/articles/${variantenId}`);
  },

  // Advanced Search
  searchArticles: (searchParams) => {
    return PlentyApiClient.post("/articles/search", searchParams);
  },

  // Statistics and Summary
  getArticlesSummary: () => {
    return PlentyApiClient.get("/articles/stats/summary");
  },

  // Convenience Search Methods
  getLowStockArticles: (threshold = 10, skip = 0, limit = 100) => {
    return PlentyApiClient.get(`/articles/search/low-stock/${threshold}`, {
      params: { skip, limit },
    });
  },

  getBestsellers: (minSales = 10, skip = 0, limit = 100) => {
    return PlentyApiClient.get(`/articles/search/bestsellers/${minSales}`, {
      params: { skip, limit },
    });
  },

  getArticlesWithoutSales: (skip = 0, limit = 100) => {
    return PlentyApiClient.get("/articles/search/no-sales", {
      params: { skip, limit },
    });
  },

  updateSheets: (source) => {
    return PlentyApiClient.post("/articles/update-sheets", { source });
  },

  // Re-categorize Articles
  categorizeArticles: () => {
    return PlentyApiClient.post("/articles/categorize");
  },

  // Cron Job Management
  startCronJob: (schedule) => {
    return PlentyApiClient.post("/articles/cron/start", null, {
      params: { schedule },
    });
  },

  stopCronJob: () => {
    return PlentyApiClient.post("/articles/cron/stop");
  },

  getCronStatus: () => {
    return PlentyApiClient.get("/articles/cron/status");
  },

  updateCronSchedule: (schedule) => {
    return PlentyApiClient.post("/articles/cron/start", null, {
      params: { schedule },
    });
  },

  // Get populate status (check if process is running)
  getPopulateStatus: () => {
    return PlentyApiClient.get("/articles/status");
  },

  // Data Population
  populateArticles: (params = {}) => {
    const { fromDate, toDate, maxItems } = params;
    const queryParams = {};

    if (fromDate) queryParams.from_date = fromDate;
    if (toDate) queryParams.to_date = toDate;
    //if (maxItems) queryParams.max_items = maxItems;

    return PlentyApiClient.get("/articles/populate", {
      params: queryParams,
    });
  },

  update_items_by_csv: (csvFile) => {
    const formData = new FormData();
    formData.append("csv_file", csvFile);

    return PlentyApiClient.post("/articles/populate-from-csv", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  // Export Articles
  exportArticles: (params = {}) => {
    const {
      format = "xlsx",
      source = null,
      article_type = null,
      sort_by = "stock_value",
      sort_order = "desc",
      latest_only = true,
    } = params;

    const queryParams = {
      format,
      sort_by,
      sort_order,
      latest_only,
    };

    if (source) queryParams.source = source;
    if (article_type) queryParams.article_type = article_type;

    return PlentyApiClient.get("/articles/export", {
      params: queryParams,
      responseType: "blob", // Important for file downloads
    });
  },

  // Helper methods for common search patterns
  searchByBusinessIntelligence: (params) => {
    const {
      salesValue,
      salesOperator = "eq",
      stockValue,
      stockOperator = "eq",
      skip = 0,
      limit = 100,
    } = params;

    const searchParams = {
      skip,
      limit,
    };

    if (salesValue !== undefined) {
      searchParams.bi_selled_articles_value = salesValue;
      searchParams.bi_selled_articles_operator = salesOperator;
    }

    if (stockValue !== undefined) {
      searchParams.bi_netto_stock_value = stockValue;
      searchParams.bi_netto_stock_operator = stockOperator;
    }

    return PlentyApiClient.post("/articles/search", searchParams);
  },

  searchBySku: (sku, skip = 0, limit = 100) => {
    return PlentyApiClient.post("/articles/search", {
      sku,
      skip,
      limit,
    });
  },

  searchByName: (articlename, skip = 0, limit = 100) => {
    return PlentyApiClient.post("/articles/search", {
      articlename,
      skip,
      limit,
    });
  },

  searchByArticleId: (articleId, skip = 0, limit = 100) => {
    return PlentyApiClient.post("/articles/search", {
      articleId,
      skip,
      limit,
    });
  },

  // Bulk operations helper
  bulkUpdateArticles: async (articles) => {
    const results = {
      success: [],
      failed: [],
    };

    for (const article of articles) {
      try {
        const result = await PlentyApiClient.put(
          `/articles/${article.variantenId}`,
          article,
        );
        results.success.push({ variantenId: article.variantenId, result });
      } catch (error) {
        results.failed.push({ variantenId: article.variantenId, error });
      }
    }

    return results;
  },

  // Eval Files Management
  listEvalFiles: () => {
    return PlentyApiClient.get("/articles/eval-files");
  },

  downloadDailyEval: (year, month, day) => {
    return PlentyApiClient.get(
      `/articles/download-eval/daily/${year}/${month}/${day}`,
      {
        responseType: "blob", // Important for file downloads
      },
    );
  },

  downloadMonthlyEval: (year, month) => {
    return PlentyApiClient.get(
      `/articles/download-eval/monthly/${year}/${month}`,
      {
        responseType: "blob", // Important for file downloads
      },
    );
  },

  downloadYearlyEval: (year) => {
    return PlentyApiClient.get(`/articles/download-eval/yearly/${year}`, {
      responseType: "blob", // Important for file downloads
    });
  },

  createEvalFile: (date) => {
    return PlentyApiClient.post("/articles/create-eval", {
      date: date,
    });
  },

  // Repricer Management
  getRepriceStatus: () => {
    return PlentyApiClient.get("/repricer/status");
  },

  manualReprice: () => {
    return PlentyApiClient.post("/repricer/manual-reprice");
  },

  getRepricerCronStatus: () => {
    return PlentyApiClient.get("/repricer/cron/status");
  },

  startRepricerCronJob: (cronExpression) => {
    return PlentyApiClient.post("/repricer/cron/start", {
      cron_expression: cronExpression,
    });
  },

  stopRepricerCronJob: () => {
    return PlentyApiClient.post("/repricer/cron/stop");
  },

  updateRepricerCronSchedule: (cronExpression) => {
    return PlentyApiClient.put("/repricer/cron/update", {
      cron_expression: cronExpression,
    });
  },
};
